import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useSession } from "@/auth/SessionContext";
import { hasUnclassifiedFindings } from "@/lib/validationCodes";
import {
  useValidationOutcome,
  useValidationStore,
  type ValidationOutcome,
} from "@/state/validationState";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import {
  hasStaleAcknowledgment,
  useFinalQuestioningQuery,
} from "./finalQuestioning";
import { queryKeys, useDraftQuery, type DraftVersionPin } from "./hooks";

/**
 * Running authoritative validation, and the gate that decides whether a
 * candidate may be frozen from the result.
 *
 * Issue #7 owns the gate; issue #8 owns the candidate-creation control that
 * consumes it. The seam is `useCandidateCreationGate`, whose `detail` string is
 * written to be attached directly to that control as its disabled reason — so
 * #8 renders a disabled control with the reason on it rather than re-deriving
 * when validation is trustworthy.
 *
 * The seam used to name `ActionBar` as the host for that reason. #516 deleted
 * that component as a noncanonical surface, so #8 supplies its own control. The
 * `detail` contract is unchanged; only the component that consumes it is open.
 */

function outcomeFromError(error: unknown): ValidationOutcome {
  if (error instanceof ApiError) {
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    // Status alone. runValidation's 409 declares no response body, so nothing
    // here may depend on reading `error.code` off it.
    if (error.isConflict) {
      return { kind: "stale", requestId: error.requestId };
    }
    if (error.isForbidden) {
      return { kind: "denied" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * Runs `POST /v1/projects/{projectId}/validations` and records the outcome.
 *
 * The pin is **always** sent when a draft version is known. That is what ties
 * the report to the exact reviewed draft rather than to whatever a later
 * autosave produced, and it is the only way the service can answer 409 instead
 * of silently validating something else.
 */
export function useRunValidation(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const beginValidation = useValidationStore((state) => state.begin);
  const setOutcomeIfCurrent = useValidationStore((state) => state.setIfCurrent);
  const draft = useDraftQuery(projectId);
  const expectedDraftVersion = draft.data?.draftVersion ?? null;
  const [isStarting, setIsStarting] = useState(false);

  const mutation = useMutation({
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      }),
    async mutationFn(input: {
      currentDraftVersion: string | null;
      revision: number;
    }) {
      const body: DraftVersionPin | null = input.currentDraftVersion
        ? { expectedDraftVersion: input.currentDraftVersion }
        : null;
      return unwrap(
        await client.POST("/v1/projects/{projectId}/validations", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    async onSuccess(report, input) {
      // The durable badge is the authority consumed after reload. Refresh it
      // before publishing the session diagnostics, and only if this run still
      // owns the project's epoch: an adoption replacement may have invalidated
      // the tuple while the validator was in flight.
      const authoritative = await draft.refetch();
      if (!authoritative.isSuccess) {
        setOutcomeIfCurrent(projectId, input.revision, {
          kind: "error",
          code: "upstream_unavailable",
          requestId: undefined,
        });
        return;
      }
      if (report.state !== authoritative.data.validationState) {
        // The badge CAS lost because another effective-configuration input
        // moved. A passing response without a durable pass must never become
        // freeze authority in this session.
        setOutcomeIfCurrent(projectId, input.revision, {
          kind: "stale",
          requestId: undefined,
        });
        return;
      }
      // Preserve diagnostics when only the draft-version coordinate disagrees.
      // The gate below compares that coordinate and blocks it as stale (or, for
      // a legacy null coordinate, as unbound) without hiding the report.
      setOutcomeIfCurrent(projectId, input.revision, {
        kind: "report",
        report,
      });
    },
    onError(error, input) {
      setOutcomeIfCurrent(projectId, input.revision, outcomeFromError(error));
    },
  });

  async function run() {
    setIsStarting(true);
    const revision = beginValidation(projectId);

    try {
      // Always refresh before submitting. In particular, the recovery after a
      // 409 must not reuse the cached pin the service just rejected.
      const currentDraft = await draft.refetch();
      mutation.mutate({
        currentDraftVersion: currentDraft.isSuccess
          ? currentDraft.data.draftVersion
          : null,
        revision,
      });
    } catch {
      // The request body is nullable by contract. If the refresh itself throws,
      // continue unpinned rather than falling back to a cached version.
      mutation.mutate({ currentDraftVersion: null, revision });
    } finally {
      setIsStarting(false);
    }
  }

  return {
    run,
    isPending: mutation.isPending || draft.isFetching,
    /** This invocation is checking its draft or submitting validation. */
    isRunning: isStarting || mutation.isPending,
    /** Null until the latest draft read has completed. */
    expectedDraftVersion,
  };
}

/**
 * Why candidate creation is refused.
 *
 * Every member is a *block*. There is no member meaning "probably fine" —
 * `allowed: true` is the only non-blocking result, and it is reachable from
 * exactly one combination of facts.
 */
export type GateBlockReason =
  | "missing_capability"
  | "draft_loading"
  | "draft_unavailable"
  | "no_draft"
  | "not_run"
  | "running"
  | "resyncing"
  | "validation_failed"
  | "unclassified_findings"
  | "unbound_draft_version"
  | "stale_draft"
  | "safety_evidence_stale"
  | "not_implemented"
  | "unavailable";

export type CandidateGateVerdict =
  | { allowed: true; draftVersion: string; pin: DraftVersionPin }
  | { allowed: false; reason: GateBlockReason; detail: string };

const BLOCK: Record<GateBlockReason, string> = {
  missing_capability:
    'Your account does not have the "preview.create" permission.',
  draft_loading:
    "The current draft is still loading. Candidate creation remains blocked until it can be verified.",
  draft_unavailable:
    "The current draft could not be loaded. Candidate creation remains blocked until it is available.",
  no_draft:
    "The project workspace could not be found. Candidate creation remains blocked.",
  not_run:
    "Run authoritative validation first. A candidate is only ever frozen from a validated effective configuration.",
  running: "Validation is still running.",
  // The blocking half of the post-refusal re-sync: the cached answer was just
  // disproven by the service, so until the fresh read lands there is nothing
  // trustworthy to open the gate from (fail-closed, like every other unknown).
  resyncing:
    "The Editor Service refused the last freeze, so this editor is re-checking the configuration's validation state. Candidate creation stays blocked until that check completes.",
  validation_failed:
    "Authoritative validation failed. Resolve the reported findings and validate again.",
  unclassified_findings:
    "The validation report contains findings this editor cannot classify, so it cannot confirm the effective configuration is clean. Report the finding codes to the Editor Service owner.",
  // #498: no implementation vocabulary — the old sentence named "draft
  // version" and an issue number, neither of which is a clinician's word for
  // anything they can act on.
  unbound_draft_version:
    "The service did not say which version it validated, so this result cannot be tied to what would be frozen. Validate again before creating a candidate.",
  stale_draft:
    "The draft changed after this report was produced. Validate again before creating a candidate.",
  // #853, claude review round 2 (P1). The service refuses a freeze whose
  // Project has final safety questioning off with a confirmation that no
  // longer covers it (`safety.final_questioning_evidence_stale`), and one of
  // the two ways that happens moves NOTHING this gate otherwise watches: a
  // deployment that revises the consequence wording makes every stored
  // acknowledgment stale while the draft, the pin set and the durable badge
  // all stand. A `passed` badge would then keep this control enabled until the
  // server refused the click, which is the dead control ADR 0001 forbids.
  safety_evidence_stale:
    "This project's final safety questioning is off, and the confirmation recorded for it no longer covers what would be frozen. Re-read the consequences on the Safety section and confirm again.",
  not_implemented:
    "Authoritative validation is not available in this environment yet, so no draft can be certified for freezing.",
  unavailable:
    "Authoritative validation could not be completed, so the draft cannot be certified for freezing.",
};

function blocked(reason: GateBlockReason): CandidateGateVerdict {
  return { allowed: false, reason, detail: BLOCK[reason] };
}

/**
 * The candidate-creation gate.
 *
 * #7's scope item is "prevent candidate creation while authoritative validation
 * is red", and its fail-closed requirement widens that: unknown or unavailable
 * validation must read as blocked, never as "probably fine". So this function
 * is written as a series of refusals with a single exit, rather than a set of
 * conditions that default to permitted.
 *
 * `allowed: true` requires **all** of:
 *  - the `preview.create` capability;
 *  - a loaded workspace (including the `dv-initial` virtual workspace);
 *  - the durable workspace badge says the current effective tuple passed;
 *  - when this session has a report, it is also clean and bound to the same
 *    workspace version.
 */
export function useCandidateCreationGate(
  projectId: string,
): CandidateGateVerdict {
  const { can } = useSession();
  const draft = useDraftQuery(projectId);
  const outcome = useValidationOutcome(projectId);
  const finalQuestioning = useFinalQuestioningQuery(projectId);

  if (!can("preview.create")) {
    return blocked("missing_capability");
  }

  // BEFORE the report, because it invalidates one: a stale disable
  // confirmation makes the freeze pipeline refuse whatever the badge says, and
  // the badge does not move when the CONTRACT is what changed.
  //
  // On a POSITIVE reading only. An unsettled or failed read leaves the rest of
  // the gate as it was, deliberately: this is the affordance, not the
  // enforcement — the service raises the finding either way — and blocking
  // every freeze in the workspace whenever one unrelated read is in flight
  // would trade a dead control for a dead surface.
  if (hasStaleAcknowledgment(finalQuestioning.data)) {
    return blocked("safety_evidence_stale");
  }

  switch (outcome.kind) {
    case "running":
      return blocked("running");
    case "resyncing":
      // The freeze refusal disproved what this session believed, and the
      // authoritative re-read has not settled. The draft query still SERVES
      // its invalidated cached data during that window (`isPending` false,
      // `isFetching` true), so falling through to the durable-badge branch
      // here would re-enable the control from the very data the service just
      // refused — the transient re-enable this state exists to close.
      return blocked("resyncing");
    case "stale":
      return blocked("stale_draft");
    case "unavailable":
      return blocked("not_implemented");
    case "denied":
    case "error":
      return blocked("unavailable");
    case "report":
      break;
    case "not_run":
      break;
  }

  if (outcome.kind === "report") {
    if (outcome.report.state !== "passed") {
      return blocked("validation_failed");
    }
    if (hasUnclassifiedFindings(outcome.report.findings)) {
      return blocked("unclassified_findings");
    }
    if (!outcome.report.draftVersion) {
      return blocked("unbound_draft_version");
    }
  }

  if (draft.isPending) {
    return outcome.kind === "not_run"
      ? blocked("not_run")
      : blocked("draft_loading");
  }

  if (draft.isError) {
    if (draft.error instanceof ApiError && draft.error.status === 404) {
      return blocked("no_draft");
    }
    return blocked("draft_unavailable");
  }

  const draftVersion = draft.data.draftVersion;
  if (!draftVersion) {
    return blocked("no_draft");
  }

  // A fresh browser has no POST response in memory. The workspace badge is
  // deliberately sufficient in that case: it is durable, server-derived and
  // bound to the full current base/adoptions/draft tuple.
  if (outcome.kind === "not_run") {
    if (draft.data.validationState !== "passed") {
      return draft.data.validationState === "failed"
        ? blocked("validation_failed")
        : blocked("not_run");
    }
    return {
      allowed: true,
      draftVersion,
      pin: { expectedDraftVersion: draftVersion },
    };
  }

  const { report } = outcome;

  // The draft moved between the report and now — the same condition the 409
  // reports, observed locally rather than by a failed request.
  if (report.draftVersion !== draftVersion) {
    return blocked("stale_draft");
  }

  return {
    allowed: true,
    draftVersion,
    pin: { expectedDraftVersion: draftVersion },
  };
}
